import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

const SWDiagnosticsCard = () => {
    const [status, setStatus] = useState({
        supported: typeof window !== 'undefined' && 'serviceWorker' in navigator,
        ready: false,
        scope: null,
        activeState: null,
        waitingState: null,
        installingState: null,
        controller: false,
        error: null,
        updatedAt: null,
    });

    const refreshStatus = async () => {
        if (!('serviceWorker' in navigator)) {
            setStatus((prev) => ({ ...prev, supported: false, updatedAt: new Date().toISOString() }));
            return;
        }

        try {
            const registration = await navigator.serviceWorker.getRegistration();
            if (!registration) {
                setStatus({
                    supported: true,
                    ready: false,
                    scope: null,
                    activeState: null,
                    waitingState: null,
                    installingState: null,
                    controller: Boolean(navigator.serviceWorker.controller),
                    error: null,
                    updatedAt: new Date().toISOString(),
                });
                return;
            }

            setStatus({
                supported: true,
                ready: true,
                scope: registration.scope || null,
                activeState: registration.active?.state || null,
                waitingState: registration.waiting?.state || null,
                installingState: registration.installing?.state || null,
                controller: Boolean(navigator.serviceWorker.controller),
                error: null,
                updatedAt: new Date().toISOString(),
            });
        } catch (err) {
            setStatus((prev) => ({
                ...prev,
                error: err?.message || String(err),
                updatedAt: new Date().toISOString(),
            }));
        }
    };

    useEffect(() => {
        refreshStatus();

        if (!('serviceWorker' in navigator)) return undefined;

        const onControllerChange = () => refreshStatus();
        navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

        return () => {
            navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
        };
    }, []);

    const summaryTone = useMemo(() => {
        if (!status.supported) return 'error';
        if (!status.ready) return 'warning';
        if (status.waitingState) return 'info';
        return 'success';
    }, [status]);

    return (
        <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1}>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Service Worker Diagnostics
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Quick runtime check for SW registration and update state
                    </Typography>
                </Box>
                <Button size="small" variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={refreshStatus} sx={{ textTransform: 'none' }}>
                    Refresh
                </Button>
            </Stack>

            <Divider sx={{ my: 1.25 }} />

            {!status.supported ? (
                <Alert severity="error" variant="outlined">Service workers are not supported in this browser.</Alert>
            ) : (
                <Stack spacing={1}>
                    <Alert severity={summaryTone} variant="outlined">
                        {!status.ready ? 'No active registration found for this origin.' : 'Service worker registration is available.'}
                    </Alert>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        <Chip size="small" color={status.ready ? 'success' : 'default'} label={`Registered: ${status.ready ? 'Yes' : 'No'}`} />
                        <Chip size="small" color={status.controller ? 'success' : 'default'} label={`Controlling page: ${status.controller ? 'Yes' : 'No'}`} />
                        {status.activeState && <Chip size="small" variant="outlined" label={`Active: ${status.activeState}`} />}
                        {status.waitingState && <Chip size="small" color="info" variant="outlined" label={`Waiting: ${status.waitingState}`} />}
                        {status.installingState && <Chip size="small" color="warning" variant="outlined" label={`Installing: ${status.installingState}`} />}
                    </Box>
                    {status.scope && (
                        <Typography variant="caption" color="text.secondary">
                            Scope: {status.scope}
                        </Typography>
                    )}
                    {status.updatedAt && (
                        <Typography variant="caption" color="text.secondary">
                            Last checked: {new Date(status.updatedAt).toLocaleString()}
                        </Typography>
                    )}
                    {status.error && <Alert severity="error" variant="outlined">{status.error}</Alert>}
                </Stack>
            )}
        </Paper>
    );
};

export default SWDiagnosticsCard;
